import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Empty, Space, Spin } from 'antd';
import type { ReportParams } from '@/services/reportDocuments.service';
import ReportDocumentActions from './ReportDocumentActions';
import ReportDocumentView from './ReportDocumentView';
import IntegrityBadge from './IntegrityBadge';
import { useReportDocument } from './useReportDocument';

interface Props {
  reportKey: string;
  /** Builder params; null shows the hint until the filters are complete. */
  params: ReportParams | null;
  /** Filter controls rendered above the toolbar. */
  filters?: React.ReactNode;
  emptyHint?: string;
  emailLabel?: string;
  defaultMessage?: string;
  /** Forwarded to ReportDocumentActions — domain send permission codes. */
  sendPermissions?: string[];
  /** Single-document send history scope. */
  documentId?: string;
  /** Preprinted stationery PDF (no letterhead bands). */
  allowPreprinted?: boolean;
  size?: 'small' | 'middle';
}

/** Filters + standard toolbar + screen preview for one server-built report document. */
const ReportDocumentPanel: React.FC<Props> = ({
  reportKey,
  params,
  filters,
  emptyHint,
  emailLabel,
  defaultMessage,
  sendPermissions,
  documentId,
  allowPreprinted,
  size,
}) => {
  const { t } = useTranslation('finance');
  const query = useReportDocument(reportKey, params);
  const doc = query.data ?? null;
  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {filters}
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <ReportDocumentActions
              reportKey={reportKey}
              params={params}
              document={doc}
              emailLabel={emailLabel}
              defaultMessage={defaultMessage}
              sendPermissions={sendPermissions}
              documentId={documentId}
              allowPreprinted={allowPreprinted}
              size={size}
            />
            {doc && <IntegrityBadge checks={doc.integrity} />}
          </Space>
        </Space>
      </Card>
      {!params ? (
        <Card>
          <Empty description={emptyHint ?? t('chooseReportCriteria')} />
        </Card>
      ) : query.isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : query.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('couldNotBuildReport')}
          description={(query.error as any)?.response?.data?.message || (query.error as Error)?.message}
        />
      ) : doc ? (
        <ReportDocumentView document={doc} />
      ) : null}
    </>
  );
};

export default ReportDocumentPanel;
